import Link from "next/link";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { FaFire, FaCheck } from "react-icons/fa";
import useLearningOverview from "./useLearningOverview";
import { ChessStreak as StreakData } from "./learningTypes";

export function ChessWeek({ streak }: { streak: Pick<StreakData, "week"> }) {
  const { t } = useTranslation("common");
  const { locale } = useRouter();
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    timeZone: "UTC",
  });
  return (
    <ul
      className="mt-4 grid grid-cols-7 gap-2"
      aria-label={t("chessStreak.week")}
    >
      {streak.week.map((day) => (
        <li
          key={day.date}
          className="flex flex-col items-center gap-2 text-xs text-slate-500"
        >
          <span>{formatter.format(new Date(`${day.date}T12:00:00Z`))}</span>
          <span
            aria-current={day.today ? "date" : undefined}
            aria-label={
              day.future
                ? day.date
                : t(
                    day.practiced
                      ? "chessStreak.activeOn"
                      : "chessStreak.inactiveOn",
                    { date: day.date }
                  )
            }
            className={`flex h-7 w-7 items-center justify-center rounded-full ${
              day.practiced
                ? "bg-amber-100 text-amber-700"
                : "bg-athens-gray-100 text-slate-400"
            } ${day.today ? "ring-2 ring-slate-300 ring-offset-2" : ""}`}
          >
            {day.practiced ? <FaCheck aria-hidden="true" /> : "·"}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ChessStreakDetails({
  streak,
  compact = false,
  showWeek = true,
}: {
  streak: StreakData;
  compact?: boolean;
  showWeek?: boolean;
}) {
  const { t } = useTranslation("common");
  const next = streak.milestones.find((item) => !item.earned);
  return (
    <div>
      <h2 className="flex items-center gap-2 font-semibold text-slate-900">
        <FaFire aria-hidden="true" className="text-orange-400" />
        {t("chessStreak.title")}
      </h2>
      <p className="mt-2 text-xl font-semibold text-slate-900">
        {t(streak.streak === 1 ? "chessStreak.oneDay" : "chessStreak.days", {
          count: streak.streak,
        })}
      </p>
      {(!compact || streak.completedToday) && (
        <p className="mt-1 text-sm text-slate-600" aria-live="polite">
          {t(
            streak.completedToday ? "chessStreak.doneToday" : "chessStreak.rule"
          )}
        </p>
      )}
      {!compact && streak.completedToday && (
        <p className="mt-2 text-xs leading-5 text-slate-500">
          {t("chessStreak.rule")}
        </p>
      )}
      {showWeek && <ChessWeek streak={streak} />}
      {!compact && showWeek && (
        <p className="mt-4 text-xs text-slate-500">
          {t("chessStreak.calendar", { timezone: streak.timezone })}
        </p>
      )}
      {!compact && next && (
        <p className="mt-2 text-sm text-slate-600">
          {t("chessStreak.nextMilestone", { count: next.target })}
        </p>
      )}
      {!compact && (
        <p className="mt-2 text-xs text-slate-500">
          {t("chessStreak.best", { count: streak.longestStreak })}
        </p>
      )}
      {compact && (
        <Link
          href="/profile/achievements/streaks"
          className="mt-3 inline-flex min-h-[44px] items-center text-sm text-altblue-600 hover:underline"
          aria-label={t("chessStreak.title")}
        >
          {t("learningProgress.details")} →
        </Link>
      )}
    </div>
  );
}

export default function ChessStreakBadge() {
  const { t } = useTranslation("common");
  const { data } = useLearningOverview();
  if (!data) return null;
  const streak = data.chessStreak;
  return (
    <details className="relative">
      <summary
        className="flex min-h-[40px] cursor-pointer list-none items-center gap-1.5 rounded-md px-1.5 text-sm font-medium text-slate-600 hover:bg-athens-gray-100 [&::-webkit-details-marker]:hidden"
        aria-label={`${t("chessStreak.title")}: ${t(
          streak.streak === 1 ? "chessStreak.oneDay" : "chessStreak.days",
          {
            count: streak.streak,
          }
        )}. ${t(
          streak.completedToday
            ? "chessStreak.doneToday"
            : "chessStreak.reminder"
        )}`}
      >
        <FaFire
          aria-hidden="true"
          className={
            streak.completedToday ? "text-orange-400" : "text-slate-400"
          }
        />
        <span>{streak.streak}</span>
        {streak.completedToday && (
          <FaCheck
            aria-hidden="true"
            className="hidden text-[10px] text-slate-500 sm:block"
          />
        )}
      </summary>
      <div className="fixed left-3 right-3 top-16 z-50 rounded-xl border border-athens-gray-200 bg-white p-4 shadow-lg md:absolute md:left-auto md:right-0 md:top-full md:mt-2 md:w-72">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <FaFire aria-hidden="true" className="shrink-0 text-orange-400" />
          {t(
            streak.streak === 0
              ? "chessStreak.start"
              : streak.streak === 1
              ? "chessStreak.oneDay"
              : "chessStreak.days",
            { count: streak.streak }
          )}
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-600" aria-live="polite">
          {t(
            streak.completedToday
              ? "chessStreak.doneToday"
              : "chessStreak.reminder"
          )}
        </p>
        <ChessWeek streak={streak} />
        <Link
          href="/profile/achievements/streaks"
          onClick={(event) =>
            event.currentTarget.closest("details")?.removeAttribute("open")
          }
          className="mt-3 inline-flex min-h-[44px] items-center text-sm font-medium text-altblue-600 hover:underline"
        >
          {t("chessStreak.history")} →
        </Link>
      </div>
    </details>
  );
}
